import { Component, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Router, RouterOutlet, NavigationStart } from '@angular/router';
import { EMPTY } from 'rxjs';
import { NavbarComponent } from '../navbar/navbar.component';
import { InsightsHeaderComponent } from '../insights-header/insights-header.component';
import { TaxonomyHeaderComponent } from '../taxonomy-header/taxonomy-header.component';
import { SharedDataService } from '../../services/shared-data.service';
import { HeaderConfig } from '../../shared/types';
import { CommonRoutes } from '../../shared/constant';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { TaxonomyService } from '../../services/taxonomy.service';
import { ActionService } from '../../services/action.service';
import { RoutePersistenceService } from '../../services/route-persistence.service';
import { DashboardNavigationService } from '../../services/dashboard-navigation.service';

@Component({
  selector: 'app-full-layout',
  standalone: true,
  imports: [
    MatSidenavModule,
    InsightsHeaderComponent,
    TaxonomyHeaderComponent,
    NavbarComponent,
    CommonModule,
    HttpClientModule,
    RouterOutlet,
    BottomSheetComponent,
  ],
  templateUrl: './full-layout.component.html',
  styleUrls: ['./full-layout.component.scss'],
})
export class FullLayoutComponent implements OnInit {
  collapsed: boolean = true;
  headerConfig: HeaderConfig = { title: 'Insights Dashboard' };
  isTaxonomyRoute: boolean = false;

  constructor(
    private sharedDataService: SharedDataService,
    private router: Router,
    private taxonomyService: TaxonomyService,
    public actionService: ActionService,
    private routePersistenceService: RoutePersistenceService,
    private dashboardNavigationService: DashboardNavigationService
  ) {}

  ngOnInit() {
    // Handle page refresh - navigate to last saved route if needed
    this.handlePageRefresh();

    const initialUrl = this.router.url.slice(1);
    this.updateHeaderTitleFromRouteLink(initialUrl);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const routeLink = event.url.slice(1);

        // Reset edit mode when navigating away from create-dashboard
        if (!routeLink.includes('create-dashboard')) {
          this.dashboardNavigationService.setEditMode(false);
        }

        // Only update header if not in edit mode (edit mode manages its own title)
        if (!this.dashboardNavigationService.isEditMode) {
          this.updateHeaderTitleFromRouteLink(routeLink);
        }

        // Clear selected taxonomy when navigating away from taxonomy routes
        // This prevents unwanted navigation effects
        this.taxonomyService.clearSelectedTaxonomyIfNotOnTaxonomyRoute();
      }
    });

    this.sharedDataService.getDashboardState().subscribe({
      next: (dashboardData) => {
        const currentRoute = this.router.url.slice(1);

        // Only redirect if:
        // 1. User is not already on a valid route
        // 2. Current route is empty or invalid
        // 3. Not already on taxonomy-dashboard or its subpages
        // 4. Dashboard state has a different route than conversation-insights
        // 5. User is not already on insights dashboard
        const isOnValidRoute = this.isValidRoute(currentRoute);
        const isOnTaxonomy = currentRoute.includes('taxonomy-dashboard');
        const isOnInsights = currentRoute === 'conversation-insights';
        const shouldRedirect = dashboardData.navBarData.routeLink !== 'conversation-insights';

        // Don't redirect if user is already on insights dashboard
        if (!isOnValidRoute && !isOnTaxonomy && !isOnInsights && shouldRedirect) {
          this.router.navigate([dashboardData.navBarData.routeLink]);
        }
      },
      error: () => this.handleError(),
    });

    // Listen for edit mode changes to update header title
    this.dashboardNavigationService.isEditMode$.subscribe((isEditMode) => {
      const currentRoute = this.router.url.slice(1);
      if (currentRoute.includes('create-dashboard')) {
        if (isEditMode) {
          // Check if it's an existing dashboard (has query param id)
          const urlTree = this.router.parseUrl(this.router.url);
          const hasId = urlTree.queryParams['id'];
          if (hasId) {
            // Existing dashboard in edit mode - show "Edit Dashboard"
            this.headerConfig.title = 'Edit Dashboard';
            this.headerConfig.showBackButton = true;
          } else {
            // New dashboard - show "Create Dashboard"
            this.headerConfig.title = 'Create Dashboard';
            this.headerConfig.showBackButton = true;
          }
        } else {
          // View mode - show "User Dashboard"
          this.headerConfig.title = 'User Dashboard';
          this.headerConfig.showBackButton = false;
        }
      }
    });
  }

  private handlePageRefresh(): void {
    const currentRoute = this.router.url.slice(1);
    const lastSavedRoute = this.routePersistenceService.getLastRoute();

    // If current route is empty or invalid, and it's not the last saved route
    if (currentRoute === '' || (!this.isValidRoute(currentRoute) && currentRoute !== lastSavedRoute)) {
      // Only redirect if the last saved route is not taxonomy-dashboard
      // This prevents forcing users back to taxonomy when they want to be on insights
      if (!this.routePersistenceService.wasLastRouteTaxonomy()) {
        this.routePersistenceService.navigateToLastRoute();
      } else {
        // If last saved route was taxonomy, default to insights dashboard
        // and clear any stored taxonomy data to prevent unwanted effects
        this.taxonomyService.setSelectedValue(null);
        this.router.navigate(['conversation-insights']);
      }
    } else if (currentRoute === 'conversation-insights') {
      // If user is refreshing on insights dashboard, clear any stored taxonomy data
      // This ensures clean state and prevents unwanted navigation effects
      this.taxonomyService.setSelectedValue(null);
    }
  }

  private isValidRoute(route: string): boolean {
    const validRoutes = [
      'conversation-insights',
      'taxonomy-dashboard',
      'taxonomy-dashboard/all-taxonomies',
      'taxonomy-dashboard/playground',
      'user-dashboard',
      'create-dashboard',
      'chart-view',
      'report',
      'settings',
      'nova',
    ];

    return validRoutes.some((validRoute) => route === validRoute || route.startsWith(validRoute + '/'));
  }

  updateHeaderTitleFromRouteLink(url: string) {
    // Don't update header if in edit mode (edit mode manages its own title)
    if (this.dashboardNavigationService.isEditMode && url.includes('create-dashboard')) {
      return;
    }

    const matchedRoute = CommonRoutes.find((route) => route.routeLink === url);

    if (matchedRoute) {
      // Special handling for create-dashboard route
      if (url.includes('create-dashboard')) {
        // Check if it's an existing dashboard (has query param id)
        const urlTree = this.router.parseUrl(this.router.url);
        const hasId = urlTree.queryParams['id'];
        if (hasId) {
          // Existing dashboard - show "User Dashboard" in view mode
          this.headerConfig.title = 'User Dashboard';
          this.headerConfig.showBackButton = false;
        } else {
          // New dashboard - show "Create Dashboard"
          this.headerConfig.title = 'Create Dashboard';
          this.headerConfig.showBackButton = true;
        }
      } else {
        this.headerConfig.title = matchedRoute.title;
        this.headerConfig.showBackButton = matchedRoute.showBackButton || false;
      }
      this.taxonomyService.updateTaxoCheckFlag(matchedRoute.title);

      switch (matchedRoute.title) {
        case 'conversation-insights':
          this.headerConfig.title = 'Insights Dashboard';
          this.isTaxonomyRoute = false;
          break;
        case 'Trends Dashboard':
          this.headerConfig.title = 'Trends Dashboard';
          this.isTaxonomyRoute = false;
          break;
        case 'Taxonomy Builder':
        case 'All Taxonomies':
        case 'Playground':
          this.headerConfig.title = 'Taxonomy Builder';
          this.isTaxonomyRoute = true;
          break;
        default:
          this.isTaxonomyRoute = false;
      }
    } else {
      if (url.includes('taxonomy-dashboard')) {
        this.headerConfig.title = 'Taxonomy Builder';
        this.isTaxonomyRoute = true;

        const routeValue: string = (matchedRoute as unknown as { routeLink: string })?.routeLink ?? '';

        if (url === 'taxonomy-dashboard' && routeValue === 'taxonomy-dashboard') {
          this.taxonomyService.updateTaxoCheckFlag('taxonomy-dashboard');
        } else {
          this.taxonomyService.updateTaxoCheckFlag('taxonomy-dashboard-subpage');
        }
      } else if (url.includes('create-dashboard')) {
        // Handle create-dashboard route even if not in CommonRoutes
        const urlTree = this.router.parseUrl(this.router.url);
        const hasId = urlTree.queryParams['id'];
        if (hasId) {
          this.headerConfig.title = 'User Dashboard';
          this.headerConfig.showBackButton = false;
        } else {
          this.headerConfig.title = 'Create Dashboard';
          this.headerConfig.showBackButton = true;
        }
        this.isTaxonomyRoute = false;
      } else if (url.includes('chart-view')) {
        // Handle chart-view route - show "User Dashboard" header
        this.headerConfig.title = 'User Dashboard';
        this.headerConfig.showBackButton = false;
        this.isTaxonomyRoute = false;
      } else {
        this.isTaxonomyRoute = false;
      }
    }
  }

  onCollapsedChanged(collapsed: boolean) {
    this.collapsed = collapsed;
  }

  handleError() {
    console.error('Failed to load dashboard data');
    return EMPTY;
  }
}
